import { DateTime } from 'luxon';

import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';

import {
  IconBriefcase,
  IconChartBar,
  IconClock,
  IconHistory,
  IconPencil,
  IconUsers,
} from '@tabler/icons-react';

import {
  Badge,
  Button,
  CardBadgeView,
  CardView,
  EmptyStateView,
  formatHours,
  Grid,
  Group,
  HistoryListView,
  IconButtonView,
  LabeledValueListView,
  Modal,
  MetricCardView,
  NumberInput,
  Stack,
  Text,
  TextInput,
} from '@optiro/ui';

import { UserAvatar } from '@/components/UserAvatar';
import { useCreateTaskHourEstimate, useTaskHourEstimates } from '@/hooks/useTasks';
import { useTaskTimesheetReport } from '@/modules/Timesheets';
import { Task } from '@/types/types';

// ─── STATUS_LABELS ────────────────────────────────────────────────────────────

const STATUS_LABELS: Record<string, string> = {
  draft: 'Udkast',
  pending: 'Afventer',
  planning: 'Planlægning',
  active: 'Aktiv',
  completed: 'Afsluttet',
};

// ─── HoursCard ────────────────────────────────────────────────────────────────

const HoursCard = ({ task }: { task: Task }) => {
  const [historyOpened, { open: openHistory, close: closeHistory }] = useDisclosure(false);
  const [addOpened, { open: openAdd, close: closeAdd }] = useDisclosure(false);
  const { data: estimates } = useTaskHourEstimates(task.id);
  const createEstimate = useCreateTaskHourEstimate(task.id);

  const latest = estimates?.[0];
  const currentHours = latest ? parseFloat(latest.hours) : null;

  const addForm = useForm({
    initialValues: {
      hours: currentHours ?? ('' as unknown as number),
      comment: '',
    },
  });

  const handleAdd = (values: typeof addForm.values) => {
    createEstimate.mutate(
      { hours: Number(values.hours), comment: values.comment || undefined },
      {
        onSuccess: () => {
          addForm.reset();
          closeAdd();
        },
      }
    );
  };

  return (
    <>
      <MetricCardView
        label="Estimeret"
        icon={<IconClock size={14} />}
        value={currentHours !== null ? formatHours(currentHours) : '–'}
        caption={
          latest
            ? `Opdateret ${DateTime.fromISO(latest.created_at).toRelative() ?? ''}`
            : 'Ingen estimat endnu'
        }
        headerAction={
          <IconButtonView icon={IconHistory} label="Se historik" onClick={openHistory} size="sm" />
        }
        footer={
          <Button
            size="xs"
            variant="light"
            mt="xs"
            leftSection={<IconPencil size={13} />}
            onClick={() => {
              addForm.setFieldValue('hours', currentHours ?? ('' as unknown as number));
              openAdd();
            }}
          >
            Opdater estimat
          </Button>
        }
      />

      <HistoryListView
        opened={historyOpened}
        onClose={closeHistory}
        icon={IconClock}
        title="Timeestimater"
        emptyTitle="Ingen timeestimater endnu"
        items={(estimates ?? []).map((e) => ({
          id: e.id,
          value: formatHours(parseFloat(e.hours)),
          badgeLabel: STATUS_LABELS[e.project_status] ?? e.project_status,
          comment: e.comment || undefined,
          timestamp: DateTime.fromISO(e.created_at).toFormat("d. LLLL yyyy 'kl.' HH:mm", {
            locale: 'da',
          }),
        }))}
      />

      <Modal opened={addOpened} onClose={closeAdd} title="Opdater timeestimat" size="sm" centered>
        <form onSubmit={addForm.onSubmit(handleAdd)}>
          <Stack gap="sm">
            <NumberInput
              label="Timer"
              placeholder="0"
              min={0}
              decimalScale={1}
              suffix="t"
              allowNegative={false}
              {...addForm.getInputProps('hours')}
            />
            <TextInput
              label="Kommentar (valgfri)"
              placeholder="Fx. efter planlægningsmøde…"
              {...addForm.getInputProps('comment')}
            />
            <Group justify="flex-end" mt="xs">
              <Button variant="default" onClick={closeAdd}>
                Annuller
              </Button>
              <Button type="submit" loading={createEstimate.isPending}>
                Gem
              </Button>
            </Group>
          </Stack>
        </form>
      </Modal>
    </>
  );
};

// ─── TimeTab ──────────────────────────────────────────────────────────────────

export const TimeTab = ({ task }: { task: Task }) => {
  const { data: timesheets } = useTaskTimesheetReport(task.id);

  if (!timesheets) return null;

  const actualHours = parseFloat(timesheets.actual_time.total_hours || '0');
  const plannedHours = timesheets.planned_time.total_hours || 0;
  const timeProgress = plannedHours > 0 ? (actualHours / plannedHours) * 100 : 0;
  const remainingHours = plannedHours - actualHours;
  const overBudget = actualHours > plannedHours;

  const byResource = Object.values(timesheets.actual_time.by_registerable);
  const plannedByResource = Object.entries(timesheets.planned_time.by_resource).flatMap(
    ([resourceType, resources]) =>
      Object.entries(resources).map(([resourceId, resource]) => ({
        key: `${resourceType}-${resourceId}`,
        ...resource,
      }))
  );

  return (
    <Stack gap="md">
      <MetricCardView
        label="Forløb"
        icon={<IconClock size={14} />}
        value={`${actualHours.toFixed(1)} / ${plannedHours} timer`}
        variant="progress"
        styling={overBudget ? 'negative' : 'neutral'}
        progress={{
          value: timeProgress,
          label: `${remainingHours.toFixed(1)} timer tilbage`,
        }}
        caption={`${timeProgress.toFixed(1)}% brugt`}
      />

      <Grid>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <HoursCard task={task} />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <MetricCardView
            label="Planlagt"
            icon={<IconClock size={14} />}
            value={formatHours(plannedHours)}
            caption="Fra Gantt-intervaller"
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <MetricCardView
            label="Faktisk"
            icon={<IconChartBar size={14} />}
            value={formatHours(actualHours)}
            styling={overBudget ? 'negative' : 'neutral'}
            caption="Fra tidsregistreringer"
          />
        </Grid.Col>
      </Grid>

      <CardView icon={{ icon: IconUsers, color: 'blue' }} title="Registreret tid efter ressource">
        {byResource.length > 0 ? (
          <Stack gap="xs">
            {byResource.map((entry) => (
              <Group
                key={`${entry.registerable_type}-${entry.registerable_id}`}
                justify="space-between"
                wrap="nowrap"
              >
                <Group gap="sm" wrap="nowrap" miw={0} flex={1}>
                  {entry.registerable_type === 'Account' ? (
                    <UserAvatar userId={entry.registerable_id} />
                  ) : (
                    <IconBriefcase size={16} color="var(--mantine-color-violet-6)" />
                  )}
                  <Text size="sm" truncate>
                    {entry.registerable_name}
                  </Text>
                </Group>
                <Group gap={6} wrap="nowrap" flex="0 0 auto">
                  <CardBadgeView label={formatHours(parseFloat(entry.total_hours))} tone="neutral" />
                  <CardBadgeView
                    label={`${formatHours(parseFloat(entry.billable_hours))} fakturerbar`}
                    tone="positive"
                  />
                </Group>
              </Group>
            ))}
          </Stack>
        ) : (
          <EmptyStateView variant="inline" title="Ingen tidsregistreringer endnu" />
        )}
      </CardView>

      <CardView icon={{ icon: IconChartBar, color: 'blue' }} title="Planlagt tid efter ressource">
        {plannedByResource.length > 0 ? (
          <LabeledValueListView
            dividers
            items={plannedByResource.map((resource) => ({
              label: resource.resource_name,
              value: (
                <Badge variant="light">
                  {formatHours(resource.total_hours)} · {resource.intervals_count} interval
                  {resource.intervals_count !== 1 ? 'ler' : ''}
                </Badge>
              ),
            }))}
          />
        ) : (
          <EmptyStateView variant="inline" title="Ingen planlagt tid endnu" />
        )}
      </CardView>
    </Stack>
  );
};
